// K2-3138: one customer session per tab - the access token as a Bearer header (never in a URL,
// which the proxy logs), one refresh at a time, and a generation that a logout ends.

export const REFRESH_LOCK_NAME = 'korzyna-customer-refresh'

export function customerAuthHeaders (token: string | null | undefined): { Authorization?: string } {
  return token ? { Authorization: `Bearer ${token}` } : {}
}

let generation = 0

export function sessionGeneration (): number {
  return generation
}

/** A refresh that started before this is stale and must not bring the session back. */
export function endSessionGeneration (): void {
  generation++
}

let logins = 0

export function loginGeneration (): number {
  return logins
}

/** user/login and social login: a new session, so a stale refresh must not revoke it. */
export function startLoginGeneration (): void {
  logins++
}

/** The customer a JWT access token names (`sub`); null for a legacy or unreadable token. */
export function tokenSubject (token: string | null | undefined): string | null {
  const parts = typeof token === 'string' ? token.split('.') : []
  if (parts.length !== 3) {
    return null
  }
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const json = typeof atob === 'function' ? atob(base64) : Buffer.from(base64, 'base64').toString('utf8')
    const sub = JSON.parse(json).sub
    return sub === undefined || sub === null ? null : String(sub)
  } catch (e) {
    return null
  }
}

/** True when both tokens name a customer and not the same one. */
export function isOtherCustomer (token: string | null | undefined, reference: string | null | undefined): boolean {
  const subject = tokenSubject(token)
  const expected = tokenSubject(reference)
  return subject !== null && expected !== null && subject !== expected
}

export function reloadPage (): void {
  window.location.reload()
}

let inFlight: Promise<any> | null = null

/** Concurrent callers (customerRequest, TaskQueue, ...) share one refresh request. */
export function sharedRefresh<T> (run: () => Promise<T>): Promise<T> {
  if (!inFlight) {
    const settle = () => { inFlight = null }
    inFlight = new Promise<T>(resolve => resolve(run())) // starts now; a throw becomes a rejection
      .then(result => { settle(); return result }, err => { settle(); throw err })
  }
  return inFlight
}

/** Other tabs wait for this tab's refresh (Web Locks, where the browser has them). */
export function withRefreshLock<T> (fn: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' && (navigator as any).locks
  return locks && typeof locks.request === 'function' ? locks.request(REFRESH_LOCK_NAME, () => fn()) : fn()
}

export interface RefreshFailure {
  code: number,
  result: any,
  definitive: boolean
}

export function refreshFailure (code: number, result: any, definitive: boolean = false): RefreshFailure {
  return { code, result, definitive }
}

/** Only vuestorefront-api rejecting the refresh token (400/401) ends the session; 5xx or network errors keep it. */
export function isDefinitiveRefreshFailure (err: any): boolean {
  return !!(err && err.definitive === true)
}

export function isDefinitiveRefreshCode (code: number): boolean {
  return code === 400 || code === 401
}
